/**
 * 介面主題（淺色／深色／跟隨系統）本機偏好。
 * 形狀對齊 userSideMsgPrefsStorage（schemaVersion／updatedAt）；獨立 key。
 * 本模組不打 HTTP。
 */

export const THEME_PREFS_SCHEMA_VERSION = 1;

export const THEME_PREFS_STORAGE_KEY = 'chenyo.userPrefs.v1.theme';

export const THEME_PREFERENCE = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

/** 未設定時預設淺色：上線後既有使用者不會因作業系統設定而突然變深色。 */
export const DEFAULT_THEME_PREFERENCE = THEME_PREFERENCE.LIGHT;

const VALID_PREFERENCES = new Set(Object.values(THEME_PREFERENCE));

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {unknown} raw
 * @returns {{ schemaVersion: number, updatedAt: string, preference: string }|null}
 */
export function normalizeThemePrefsBlob(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }

  if (Number(raw.schemaVersion) !== THEME_PREFS_SCHEMA_VERSION) {
    return null;
  }

  if (!VALID_PREFERENCES.has(raw.preference)) {
    return null;
  }

  return {
    schemaVersion: THEME_PREFS_SCHEMA_VERSION,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
    preference: raw.preference,
  };
}

/**
 * @returns {{ schemaVersion: number, updatedAt: string, preference: string }|null}
 */
export function readThemePrefs() {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  try {
    const raw = localStorage.getItem(THEME_PREFS_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeThemePrefsBlob(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * @param {'light'|'dark'|'system'} preference
 * @returns {{ schemaVersion: number, updatedAt: string, preference: string }|null}
 */
export function writeThemePrefs(preference) {
  if (typeof localStorage === 'undefined' || !VALID_PREFERENCES.has(preference)) {
    return null;
  }

  const next = {
    schemaVersion: THEME_PREFS_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    preference,
  };

  try {
    localStorage.setItem(THEME_PREFS_STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}

/**
 * 偏好 → 實際套用的主題。
 * @param {string} preference
 * @param {boolean} systemPrefersDark
 * @returns {'light'|'dark'}
 */
export function resolveTheme(preference, systemPrefersDark) {
  if (preference === THEME_PREFERENCE.DARK) {
    return 'dark';
  }
  if (preference === THEME_PREFERENCE.SYSTEM) {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return 'light';
}
